import { useCallback, useState } from "react";
import { Item, SpacePermission } from "@thenounproject/lingo-core";

import useGenerateFilecut from "@actions/useGenerateFilecut";
import useNotifications from "@actions/useNotifications";
import useDownloadFile from "@actions/useDownloadFile";
import useNavPoint from "@hooks/useNavPoint";
import { IconStatus } from "../types";
import { useDownloadErrorHandler } from "./useDownloadErrorHandler";

export type DownloadStatus = {
  fileType: string;
  status: Exclude<IconStatus, null>;
} | null;

export function useFileExport(item: Item) {
  const [downloadStatus, setDownloadStatus] = useState<DownloadStatus>(null);
  const [generateFilecut] = useGenerateFilecut();
  const { showNotification } = useNotifications();
  const { handleDownloadError } = useDownloadErrorHandler();
  const downloadFile = useDownloadFile();
  const { space, portalId } = useNavPoint();

  const downloadAsset = useCallback(
    async (fileType: string, dimensions: string, dpi?: number) => {
      const asset = item.asset;
      if (!asset) return;

      const assetName = asset.name || asset.meta.font?.displayName || "Unnamed asset";

      const useLibrary =
        space?.access?.permissions?.includes(SpacePermission.viewAssetLibrary) === true;

      showNotification({
        message: `Preparing ${assetName} ${fileType}${dimensions !== "1x" ? ` @ ${dimensions}` : ""} for download...`,
      });

      setDownloadStatus({ fileType, status: "loading" });

      const action = await generateFilecut({
        inspectable: { item, asset },
        fileType,
        dimensions,
        dpi,
        portalId,
        isAdmin: useLibrary,
        userAction: "download",
      });

      if (action.error) {
        setDownloadStatus({ fileType, status: "error" });

        const handled = handleDownloadError(action.error, {
          onRetry: () => downloadAsset(fileType, dimensions, dpi),
        });

        if (!handled) {
          showNotification({
            message:
              action.error.message ||
              "There was a problem preparing your file for download. Please try again.",
            level: "error",
            link: {
              text: "View asset.",
              url: asset.permalink,
            },
          });
        }
      } else {
        showNotification({ message: "Your download should begin in just a second" });
        downloadFile(action.response.result.url);
        setDownloadStatus({ fileType, status: "success" });
      }

      setTimeout(() => {
        setDownloadStatus(null);
      }, 3000);
    },
    [
      item,
      space?.access?.permissions,
      generateFilecut,
      portalId,
      showNotification,
      handleDownloadError,
      downloadFile,
    ]
  );

  return {
    downloadAsset,
    downloadStatus,
    isDownloading: downloadStatus?.status === "loading",
  };
}
